import type { ReactNode } from 'react';

import { createContext, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

interface TopbarContextType {
  buttonsDisabled: boolean;
  pageTitle: string;
  testId?: string;
}

export const TopbarContext = createContext<TopbarContextType>({
  buttonsDisabled: false,
  pageTitle: '',
});

export type TopbarButtonsMobilePlacement = 'top' | 'bottom';

const UtilityToolbar = ({
  children,
  buttonsMobilePlacement,
}: {
  children: ReactNode;
  buttonsMobilePlacement?: TopbarButtonsMobilePlacement;
}) => (
  <div
    className={twMerge(
      'flex items-center justify-center lg:justify-end',
      buttonsMobilePlacement === 'top'
        ? 'space-x-1.5 '
        : `space-x-3 fixed bottom-0 left-0 w-full border-t border-slate-100 dark:border-slate-800
           lg:relative lg:w-auto lg:border-none pt-2 pb-safe-or-2 lg:py-0 pl-4 2xl:pl-6
           bg-white dark:bg-slate-950 lg:bg-transparent lg:dark:bg-transparent`,
    )}
  >
    {children}
  </div>
);

const StatusUtilityToolbar = ({ children }: { children: ReactNode }) => (
  <div
    className={twMerge(
      'hidden lg:flex items-center justify-center gap-3 shrink-0 px-4 2xl:px-6',
      'border-r border-slate-100 dark:border-slate-800',
    )}
  >
    {children}
  </div>
);

const defaultArrayProp: ReactNode[] = [];

interface TopbarProps {
  /**
   * Topbar logo
   */
  topbarLogo?: ReactNode;
  /**
   * Header for this section
   */
  topbarHeading?: string;
  /**
   * Buttons passed to topbar
   */
  buttons?: ReactNode;
  /**
   * Breadcrumbs
   */
  breadcrumbs?: ReactNode;
  /**
   * If topbar buttons are disabled
   */
  buttonsDisabled?: boolean;
  /**
   * Status component to display
   */
  status?: ReactNode;
  /**
   * Content to display before heading
   */
  beforeHeading?: ReactNode;
  /**
   * Placement of buttons on mobile devices,
   * default: fixed at the bottom of the screen
   */
  buttonsMobilePlacement?: 'top' | 'bottom';
  /**
   * Additional CSS classes
   */
  additionalClasses?: string;
  /**
   * Component test id
   */
  testId?: string;
}

const Topbar = ({
  buttonsDisabled = false,
  topbarLogo = '',
  topbarHeading = '',
  buttons = defaultArrayProp,
  status,
  breadcrumbs = defaultArrayProp,
  beforeHeading,
  additionalClasses,
  buttonsMobilePlacement = 'bottom',
  testId,
}: TopbarProps) => {
  const topbarContext = useMemo(
    () => ({ pageTitle: topbarHeading, buttonsDisabled, testId }),
    [buttonsDisabled, testId, topbarHeading],
  );

  return (
    <TopbarContext.Provider value={topbarContext}>
      <header
        className={twMerge(
          'flex items-center border-b border-slate-100 dark:border-none sm:border-none bg-white dark:bg-slate-950',
          'sticky z-19 w-full',
          'transition-all ease-in-out duration-normal',
          additionalClasses,
        )}
        {...getTestProps(testId)}
        style={{ top: 'var(--flotiqLayoutOffset)' }}
      >
        <div className="h-full inline-flex sm:hidden items-center pl-3 sm:pl-none">
          {topbarLogo}
        </div>
        <div
          className="inline-flex h-14 px-3 lg:px-7 justify-between items-center
      border-b-none sm:border-b sm:border-slate-100 sm:dark:border-slate-950 grow min-w-0 max-w-full"
        >
          <div className="flex justify-between min-w-0 w-full">
            <div className="min-w-0 w-full">
              <div
                className="flex items-center max-w-[calc(100vw-94px)]
                          sm:max-w-[calc(100vw-114px)] lg:max-w-[calc(100vw-400px)]"
              >
                {beforeHeading}
                <h1
                  className={twMerge(
                    'text-lg sm:text-2xl font-bold w-fit m-0 p-0 leading-6 truncate dark:text-white',
                    'lg:max-w-full',
                  )}
                  title={topbarHeading.length > 30 ? topbarHeading : ''}
                >
                  {topbarHeading}
                </h1>
              </div>
              {breadcrumbs}
            </div>
            {status && <StatusUtilityToolbar>{status}</StatusUtilityToolbar>}
          </div>
          <UtilityToolbar buttonsMobilePlacement={buttonsMobilePlacement}>
            {buttons}
          </UtilityToolbar>
        </div>
      </header>
    </TopbarContext.Provider>
  );
};

export default Topbar;
